<template>
  <div id="edit-full-text-update" class="vasion-white-background">
    <div id="edit-full-text-update-header">
      <span id="header">{{ schedulerTitle }}</span>

      <VasionButton
        id="save-button"
        class="add-cancel"
        name="save-button"
        classProp="primary"
        :isDisabled="!isDirty"
        @vasionButtonClicked="save()"
      >
        Save
      </VasionButton>

      <VasionButton
        id="cancel-button"
        class="add-cancel"
        name="cancel-button"
        classProp="secondary"
        @vasionButtonClicked="cancel()"
      >
        Cancel
      </VasionButton>
    </div>

    <div id="main-section">
      <div id="full-text-update-details">
        <AutomationEngineSharedHeader
          ref="header"
          :nameLabel="'Full Text Update Name'"
          @permissions="showMainSection = false"
          @general-automation="showMainSection = true"
          @dirty="isDirty = true"
        />
        <div v-if="showMainSection" id="full-text-update-section" class="collapsable-section">
          <h2 class="subheader">
            Build Full Text Update
          </h2>
        </div>
      </div>

      <div v-if="showMainSection" id="build-section">
        <div class="row">
          <md-radio v-model="applyToType" value="all" @change="isDirty = true">
            Apply on All Searches
          </md-radio>
          <md-radio v-model="applyToType" value="single" @change="isDirty = true">
            Apply on Specific Search
          </md-radio>
        </div>

        <div v-show="applyToType === 'single'" class="row">
          <VasionDropList
            v-slot="slotItem"
            v-model="searchItem"
            class="row-field"
            :dataArray="searchForDropList"
            width="396"
            title="SEARCH NAME"
            placeholder="Select Search..."
            type="plain-list"
            displayName="name"
            valueName="value"
            @input="isDirty = true"
          >
            {{ slotItem.item.name }}
          </VasionDropList>
        </div>
      </div>
    </div>

    <md-dialog id="confirmLeave" :md-active.sync="showLeaveDialog" @md-clicked-outside="clickedOutsideLeave()">
      <VasionConfirmationDialog
        message="Are you sure you want to leave? Changes have not been saved."
        @noButtonClick="cancelLeave()"
        @yesButtonClick="doLeave()"
      />
    </md-dialog>

    <VasionSnackbar
      id="results-snack"
      :showSnackbarBool.sync="showSnackbar"
      :snackbarImage="snackbarImage"
      :snackbarSubTitle="snackbarText"
      :snackbarTitle="snackbarTitle"
    />
  </div>
</template>

<script>
/* eslint-disable valid-typeof */
/* eslint-disable no-mixed-operators */

import AutomationEngineSharedHeader from '@/views/admin/automationEngine/AutomationEngineSharedHeader.vue'
import { SearchTypes } from '@/enums/searchTypes'

export default {
  name: 'EditFullTextUpdate',
  components: {
    AutomationEngineSharedHeader,
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty && !this.routeTo) {
      this.routeTo = to
      this.toggleLeaveDialog()
    } else {
      this.routeTo = null
      next()
    }
  },
  data: function () {
    return {
      applyToType: 'all',
      isDirty: false,
      schedulerId: 0,
      schedulerTitle: '',
      showLeaveDialog: false,
      showMainSection: true,
      showSnackbar: false,
      searchItem: '',
      snackbarImage: false,
      snackbarTitle: '',
      snackbarText: '',
    }
  },
  computed: {
    activeScheduler() { return this.$store.state.automationEngine.activeScheduler },
    applyOnAllSearches() { return this.applyToType === 'all' },
    searchForDropList() {
        return this.searchList.map((search) => {
        return {
          name: search.Name,
          value: search.SearchId,
        }
      })
    },
    searchList() {
      return this.searchListTypeID <= 0 ? this.$store.state.search.adminSearchList : this.$store.state.search.adminSearchList.filter((search) => {
        return this.searchListTypeID === search.Type
      })
    },
    searchListTypeID() { return SearchTypes.FullText },
  },
  watch: {},
  async created() {
    await this.$store.dispatch('automationEngine/clearActiveScheduler')
    this.$store.dispatch('search/loadAdminSearchList')

    if (this.$route.params.schedulerId && parseInt(this.$route.params.schedulerId, 10) > 0) {
      await this.$store.dispatch('automationEngine/getFullTextUpdateScheduler', parseInt(this.$route.params.schedulerId, 10))
      if (this.activeScheduler) {
        this.setSchedulerValues()
      }
    }
    this.setHeaderDetails(this.schedulerId, this.activeScheduler?.runDetails)

    this.schedulerTitle = this.schedulerId && this.schedulerId > 0 && this.activeScheduler.runDetails ? this.activeScheduler.runDetails.name : 'Untitled Full Text Update'
  },
  methods: {
    cancel() { this.$router.push({ name: 'AutomationEngineFullTextUpdate' }) },
    cancelLeave() {
      this.routeTo = null
      this.toggleLeaveDialog()
    },
    clickedOutsideLeave() { this.routeTo = null },
    async doLeave() {
      this.toggleLeaveDialog()
      this.$router.push({ path: this.routeTo.path })
    },
    hideSnackbar() { this.showSnackbar = false },
    async save() {
      if (this.applyOnAllSearches) {
        this.searchItem = ''
      }
      const { header } = this.$refs
      if (await this.validate(header)) {
        const payload = {
          runDetails: await header.getValues(),
          applyOnAllSearches: this.applyOnAllSearches,
          searchID: this.searchItem.value,
        }

        if (!await this.$store.dispatch('automationEngine/saveFullTextUpdateScheduler', payload)) {
          const errorText = 'There was an error saving the Full Text Update.'
          this.snackbarTitle = 'ERROR'
          this.snackbarText = errorText
          this.showSnackbar = true
          this.snackbarImage = false
          console.warn(errorText)
        } else {
          this.isDirty = false
          this.snackbarTitle = 'SUCCESS'
          this.snackbarText = 'Full Text Update saved successfully'
          this.showSnackbar = true
          this.snackbarImage = true
          this.$router.push({ name: 'AutomationEngineFullTextUpdate' })
        }
      }
    },
    setHeaderDetails(id, details) {
      const { header } = this.$refs
      if (id > 0) {
        header.setValues(details)
      } else {
        header.setValues()
      }
    },
    setSchedulerValues() {
      // Set all the non-header values here, for an existing scheduler
      this.schedulerId = this.activeScheduler.runDetails.schedulerID
      this.applyToType = this.activeScheduler.applyOnAllSearches ? 'all' : 'single'
      this.searchItem = this.searchForDropList.find(a => a.value === this.activeScheduler.searchID) || ''
    },
    toggleCheckbox(id) {
      this[id] = !this[id]
      this.isDirty = true
    },
    toggleLeaveDialog() { this.showLeaveDialog = !this.showLeaveDialog },
    async validate(header) {
      let error = false
      let errorText = 'ERROR: '

      await header.validate()
      if (header.errors !== '') {
        error = true
        errorText = `${errorText} ${header.errors}`
      }
      if (!this.applyOnAllSearches && !this.searchItem) {
          error = true
          errorText = 'Please select a Search'
      }
      if (error) {
        this.snackbarTitle = 'ERROR'
        this.snackbarText = errorText
        this.showSnackbar = true
        this.snackbarImage = false
        console.warn(errorText)
        return false
      }

      return true
    },
  },
}
</script>

<style lang="scss">
  @import '@/assets/css/variables.scss';

  #edit-full-text-update {
    width: 100%;
    margin: 15px;

    #edit-full-text-update-header {
      width: 100%;
      height: 48px;
      margin-bottom: 5px;

      #header {
        @include Headline;
      }

      .add-cancel {
        float: right;
      }
    }

    #main-section {
      margin-top: 10px;

      #full-text-update-details {
        width: 100%;
        .row {
          width: 100%;
          margin: 13px 0;
          display: flex;

          .section-title {
            @include SECTION-HEADER;
            color: $grey-400;
            display: block;
            margin: 5px 0;
          }

          .subheader {
            @include SubHeadline;
          }

          .row-field {
            margin: 0 14px 5px 0;

            &.checkbox {
              width: 390px;
            }
          }

          .browse-input {
            margin-right: 5px;
          }

          .vasion-button {
            &.browse-button {
              button {
                margin-top: 22px;
                margin-left: 0;
                margin-right: 13px;
              }
            }
          }

          .scheduler-checkbox {
            width: 408px;
          }
        }

        .collapsable-section {
          float: left;
          margin-top: 20px;
          width: 100%;

          .subheader {
            @include SubHeadline;
            width: 225px;
            display: block;
            float: left;
            margin-top: 5px;
          }

          .subtitle {
            @include Subtitle;
          }
        }
      }
    }
  }

  #build-section {
    padding-left: 15px;
  }
</style>
